import React, { createContext, useContext, useState, useMemo, useCallback } from "react";
import { defaultTrailer, defaultCustomer, defaultPrimo, defaultClient } from "@/lib/simulation-defaults";

const SimulationContext = createContext(null);

const emptyState = () => ({
  id: null, // if editing an existing saved simulation
  client: { ...defaultClient },
  trailer: { ...defaultTrailer },
  customer: { ...defaultCustomer },
  primo: { ...defaultPrimo },
});

export function SimulationProvider({ children }) {
  const [phase, setPhase] = useState(1);
  const [state, setState] = useState(emptyState);

  const updateClient = useCallback((patch) => {
    setState((s) => ({ ...s, client: { ...s.client, ...patch } }));
  }, []);

  const updateTrailer = useCallback((patch) => {
    setState((s) => ({ ...s, trailer: { ...s.trailer, ...patch } }));
  }, []);

  const updateCustomer = useCallback((patch) => {
    setState((s) => ({ ...s, customer: { ...s.customer, ...patch } }));
  }, []);

  const updatePrimo = useCallback((patch) => {
    setState((s) => ({ ...s, primo: { ...s.primo, ...patch } }));
  }, []);

  const loadSimulation = useCallback((sim) => {
    setState({
      id: sim.id,
      client: { ...defaultClient, ...(sim.client || {}) },
      trailer: { ...defaultTrailer, ...(sim.trailer || {}) },
      customer: { ...defaultCustomer, ...(sim.customer || {}) },
      primo: { ...defaultPrimo, ...(sim.primo || {}) },
    });
    setPhase(4); // jump to comparison
  }, []);

  /**
   * Duplicate an existing simulation as a NEW editable draft.
   * - Clears the server-side id so `Save` creates a fresh record
   * - Copies every field (trailer/customer/primo/client) from the source
   * - Appends a "(Copy)" suffix to the client's company name so the two are
   *   visually distinct in the Saved list once the duplicate is re-saved
   * - Jumps the wizard to Phase 1 so the salesperson can review & edit
   *   the client info before saving.
   */
  const duplicateSimulation = useCallback((sim, copySuffix = "(Copy)") => {
    const baseName = (sim.client && sim.client.company_name) || "";
    const newName = baseName
      ? (baseName.includes(copySuffix) ? baseName : `${baseName} ${copySuffix}`.trim())
      : "";
    setState({
      id: null, // brand-new draft — saving will POST, not PUT
      client: { ...defaultClient, ...(sim.client || {}), company_name: newName },
      trailer: { ...defaultTrailer, ...(sim.trailer || {}) },
      customer: { ...defaultCustomer, ...(sim.customer || {}) },
      primo: { ...defaultPrimo, ...(sim.primo || {}) },
    });
    setPhase(1); // start from configuration so the client info can be edited
  }, []);

  const resetSimulation = useCallback(() => {
    setState(emptyState());
    setPhase(1);
  }, []);

  const next = useCallback(() => setPhase((p) => Math.min(5, p + 1)), []);
  const back = useCallback(() => setPhase((p) => Math.max(1, p - 1)), []);
  const goTo = useCallback((p) => setPhase(Math.max(1, Math.min(5, p))), []);

  const value = useMemo(
    () => ({
      phase, next, back, goTo, setPhase,
      state, setState,
      updateClient, updateTrailer, updateCustomer, updatePrimo,
      loadSimulation, duplicateSimulation, resetSimulation,
    }),
    [phase, state, updateClient, updateTrailer, updateCustomer, updatePrimo, loadSimulation, duplicateSimulation, resetSimulation, next, back, goTo]
  );

  return <SimulationContext.Provider value={value}>{children}</SimulationContext.Provider>;
}

export function useSimulation() {
  const ctx = useContext(SimulationContext);
  if (!ctx) throw new Error("useSimulation must be used within SimulationProvider");
  return ctx;
}
